import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import CoachDashboard from './pages/CoachDashboard';
import CoachManagement from './pages/CoachManagement';
import LocationBatchPage from './pages/LocationBatchPage';
import CycleManagement from './pages/CycleManagement';
import TemplateManagement from './pages/TemplateManagement';
import BrandingSettings from './pages/BrandingSettings';
import AllEvaluations from './pages/AllEvaluations';
import AuditLogs from './pages/AuditLogs';
import NewEvaluation from './pages/NewEvaluation';
import EvaluationDetail from './pages/EvaluationDetail';
import MyEvaluations from './pages/MyEvaluations';
import AnalyticsPage from './pages/AnalyticsPage';


// Protected Route — enforces authentication + optional role check
function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1117] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#2BB5D8] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // If a specific role is required, redirect to the user's own dashboard
  if (role && user.role !== role) {
    const home = user.role === 'admin' ? '/admin/dashboard' : '/coach/dashboard';
    return <Navigate to={home} replace />;
  }

  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/coach/dashboard'} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RoleRedirect />} />

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/coaches" element={<ProtectedRoute role="admin"><CoachManagement /></ProtectedRoute>} />
      <Route path="/admin/locations" element={<ProtectedRoute role="admin"><LocationBatchPage /></ProtectedRoute>} />
      <Route path="/admin/cycles" element={<ProtectedRoute role="admin"><CycleManagement /></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute role="admin"><TemplateManagement /></ProtectedRoute>} />
      <Route path="/admin/branding" element={<ProtectedRoute role="admin"><BrandingSettings /></ProtectedRoute>} />

      <Route path="/admin/evaluations" element={<ProtectedRoute role="admin"><AllEvaluations /></ProtectedRoute>} />
      <Route path="/admin/evaluations/:id" element={<ProtectedRoute role="admin"><EvaluationDetail /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute role="admin"><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute role="admin"><AuditLogs /></ProtectedRoute>} />

      {/* Coach Routes */}
      <Route path="/coach/dashboard" element={<ProtectedRoute role="coach"><CoachDashboard /></ProtectedRoute>} />
      <Route path="/coach/new-evaluation" element={<ProtectedRoute role="coach"><NewEvaluation /></ProtectedRoute>} />
      <Route path="/coach/evaluations" element={<ProtectedRoute role="coach"><MyEvaluations /></ProtectedRoute>} />
      <Route path="/coach/evaluations/:id" element={<ProtectedRoute role="coach"><EvaluationDetail /></ProtectedRoute>} />
      <Route path="/coach/analytics" element={<ProtectedRoute role="coach"><AnalyticsPage /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
